/**
 * Application Theme Configuration
 * 
 * Integrates semantic color tokens with Material-UI's theme system
 */

import { createTheme, ThemeOptions } from '@mui/material/styles';
import type { PaletteMode } from '@mui/material';
import { createColorTokens } from './tokens';
import { getColorScheme, lightenColor, darkenColor } from './colorSchemes';

/**
 * Creates the application theme with semantic color tokens
 */
export const createAppTheme = (mode: PaletteMode, colorSchemeId: string = 'default') => {
  const scheme = getColorScheme(colorSchemeId);
  const tokens = createColorTokens(mode);
  const isLight = mode === 'light';

  // Generate color variations for the selected scheme
  const primaryMain = scheme.colors.primary;
  const primaryLight = isLight ? lightenColor(primaryMain, 20) : lightenColor(primaryMain, 10);
  const primaryDark = isLight ? darkenColor(primaryMain, 20) : darkenColor(primaryMain, 10);

  const secondaryMain = scheme.colors.secondary;
  const secondaryLight = isLight ? lightenColor(secondaryMain, 20) : lightenColor(secondaryMain, 10);
  const secondaryDark = isLight ? darkenColor(secondaryMain, 20) : darkenColor(secondaryMain, 10);

  const themeOptions: ThemeOptions = {
    palette: {
      mode,
      
      // Primary brand color (from selected scheme)
      primary: {
        main: primaryMain,
        light: primaryLight,
        dark: primaryDark,
        contrastText: '#ffffff',
      },
      
      // Secondary brand color (from selected scheme)
      secondary: {
        main: secondaryMain,
        light: secondaryLight,
        dark: secondaryDark,
        contrastText: '#ffffff',
      },
      
      // Semantic colors
      success: {
        main: tokens.semantic.success.main,
        light: tokens.semantic.success.background,
        dark: tokens.semantic.success.border,
        contrastText: '#ffffff',
      },
      warning: {
        main: tokens.semantic.warning.main,
        light: tokens.semantic.warning.background,
        dark: tokens.semantic.warning.border,
        contrastText: '#ffffff',
      },
      error: {
        main: tokens.semantic.error.main,
        light: tokens.semantic.error.background,
        dark: tokens.semantic.error.border,
        contrastText: '#ffffff',
      },
      info: {
        main: tokens.semantic.info.main,
        light: tokens.semantic.info.background,
        dark: tokens.semantic.info.border,
        contrastText: '#ffffff',
      },
      
      // Background colors
      background: {
        default: tokens.background.default,
        paper: tokens.background.paper,
      },
      
      // Text colors
      text: {
        primary: tokens.text.primary,
        secondary: tokens.text.secondary,
        disabled: tokens.text.disabled,
      },
      
      // Divider color
      divider: tokens.border.default,
      
      // Action states
      action: {
        active: tokens.text.primary,
        hover: tokens.component.sidebar.hover,
        selected: tokens.component.sidebar.active,
        disabled: tokens.text.disabled,
        disabledBackground: tokens.component.input.disabled,
        focus: isLight ? 'rgba(46, 134, 171, 0.12)' : 'rgba(46, 134, 171, 0.24)',
      },
    },
    
    // Typography
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      h1: {
        fontWeight: 700,
        fontSize: '2.5rem',
        lineHeight: 1.2,
        color: tokens.text.primary,
      },
      h2: {
        fontWeight: 700,
        fontSize: '2rem',
        lineHeight: 1.3,
        color: tokens.text.primary,
      },
      h3: {
        fontWeight: 600,
        fontSize: '1.75rem',
        lineHeight: 1.4,
        color: tokens.text.primary,
      },
      h4: {
        fontWeight: 600,
        fontSize: '1.5rem',
        lineHeight: 1.4,
        color: tokens.text.primary,
      },
      h5: {
        fontWeight: 600,
        fontSize: '1.25rem',
        lineHeight: 1.5,
        color: tokens.text.primary,
      },
      h6: {
        fontWeight: 600,
        fontSize: '1rem',
        lineHeight: 1.6,
        color: tokens.text.primary,
      },
      body1: {
        fontSize: '1rem',
        lineHeight: 1.5,
        color: tokens.text.primary,
      },
      body2: {
        fontSize: '0.875rem',
        lineHeight: 1.57,
        color: tokens.text.secondary,
      },
      caption: {
        fontSize: '0.75rem',
        lineHeight: 1.66,
        color: tokens.text.tertiary,
      },
      overline: {
        fontSize: '0.75rem',
        fontWeight: 600,
        lineHeight: 2.66,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        color: tokens.text.tertiary,
      },
    },
    
    // Shape
    shape: {
      borderRadius: 8,
    },
    
    // Shadows
    shadows: [
      'none',
      tokens.shadow.sm,
      tokens.shadow.sm,
      tokens.shadow.md,
      tokens.shadow.md,
      tokens.shadow.md,
      tokens.shadow.lg,
      tokens.shadow.lg,
      tokens.shadow.lg,
      tokens.shadow.xl,
      tokens.shadow.xl,
      tokens.shadow.xl,
      tokens.shadow.xl,
      tokens.shadow.xl,
      tokens.shadow.xl,
      tokens.shadow.xl,
      tokens.shadow.xl,
      tokens.shadow.xl,
      tokens.shadow.xl,
      tokens.shadow.xl,
      tokens.shadow.xl,
      tokens.shadow.xl,
      tokens.shadow.xl,
      tokens.shadow.xl,
      tokens.shadow.xl,
    ],
    
    // Component overrides
    components: {
      // Button
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 6,
            padding: '8px 16px',
          },
          contained: {
            boxShadow: 'none',
            '&:hover': {
              boxShadow: tokens.shadow.sm,
            },
          },
          outlined: {
            borderColor: tokens.border.default,
            '&:hover': {
              borderColor: tokens.border.strong,
              backgroundColor: tokens.component.card.hover,
            },
          },
        },
      },
      
      // Card
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            border: `1px solid ${tokens.border.default}`,
            boxShadow: tokens.shadow.sm,
            '&:hover': {
              boxShadow: tokens.shadow.md,
            },
          },
        },
      },
      
      // Paper
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
          elevation1: {
            boxShadow: tokens.shadow.sm,
          },
          elevation2: {
            boxShadow: tokens.shadow.md,
          },
          elevation3: {
            boxShadow: tokens.shadow.lg,
          },
        },
      },
      
      // Input
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: tokens.component.input.border,
              },
              '&:hover fieldset': {
                borderColor: tokens.border.strong,
              },
              '&.Mui-focused fieldset': {
                borderColor: tokens.border.focus,
              },
            },
          },
        },
      },
      
      // Select
      MuiSelect: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: tokens.component.input.border,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: tokens.border.strong,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: tokens.border.focus,
            },
          },
        },
      },
      
      // Table
      MuiTableHead: {
        styleOverrides: {
          root: {
            backgroundColor: tokens.component.table.headerBackground,
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:nth-of-type(odd)': {
              backgroundColor: tokens.component.table.rowBackgroundAlt,
            },
            '&:hover': {
              backgroundColor: tokens.component.table.rowHover,
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderColor: tokens.border.subtle,
          },
        },
      },
      
      // Tooltip
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: tokens.component.tooltip.background,
            color: tokens.component.tooltip.text,
            fontSize: '0.75rem',
            padding: '8px 12px',
            borderRadius: 6,
          },
        },
      },
      
      // Dialog
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundImage: 'none',
            boxShadow: tokens.shadow.xl,
          },
        },
      },
      
      // Chip
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 6,
          },
        },
      },
      
      // Tabs
      MuiTabs: {
        styleOverrides: {
          indicator: {
            height: 3,
          },
        },
      },
      
      // Drawer
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRight: `1px solid ${tokens.border.default}`,
          },
        },
      },
    },
    
    // Add our custom tokens to the theme
    tokens,
  };

  return createTheme(themeOptions);
};

